/**
 * Compute Jobs Table Component for Home Page
 */

import React from 'react';
import './compute-jobs-table.css';
import '../../App.css';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

// icons
import refreshIcon from '../../icons/refresh.512.png';

// classes
import StatusIndicator from '../../tools/status-indicator';
import MiniButton from '../../tools/mini-button';
import PleaseWait from '../../tools/please-wait';
// types
import { ComputeJob } from '../compute-jobs/types';
import { Event, EventType, EventTarget } from '../../utils/events';
import { handleEvent } from '../../app-actions';

/**
 * Get status badge color based on job status
 */
const getStatusColor = (status: string): string => {
  const statusLower = status.toLowerCase();

  if (statusLower.includes('running') || statusLower.includes('active')) {
    return 'status-running';
  }
  if (
    statusLower.includes('completed') ||
    statusLower.includes('finished') ||
    statusLower.includes('done')
  ) {
    return 'status-completed';
  }
  if (statusLower.includes('failed') || statusLower.includes('error')) {
    return 'status-failed';
  }
  if (
    statusLower.includes('pending') ||
    statusLower.includes('submitted') ||
    statusLower.includes('queued')
  ) {
    return 'status-pending';
  }

  return 'status-unknown';
};

export default function ComputeJobsTable() {
  const dispatch = useAppDispatch();

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  // global state variables.
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  // data state variables.
  const sD_computeJobs: ComputeJob[] = useAppSelector((state) => state.data.computeJobs);
  const sD_computeJobsLoading: boolean = useAppSelector(
    (state) => state.data.computeJobsLoading,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Handler for changes to the radio buttons.
  //
  //	------------------------------------------------------------

  const onClickHandler = (event: React.MouseEvent<HTMLElement>) => {
    const inputBox = event.currentTarget;

    // refresh jobs is pressed, so get a list of jobs from the API.
    if (inputBox.id === 'refresh-page') {
      //
      // get the list of compute jobs.
      const newEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.GET_COMPUTE_JOBS,
      };
      dispatch(handleEvent({ event: newEvent }));
      //
    }
    //
  }; // onClickHandler

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  if (
    sD_computeJobs.length === 0 &&
    sD_computeJobsLoading === false &&
    sG_loggedIn === true
  ) {
    return <div className="no-data-overlay">No compute jobs</div>;
  }

  if (sG_loggedIn !== true) {
    return <div className="no-data-overlay">Login required</div>;
  }

  return (
    <div
      className="compute-jobs-table-container"
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: '0px',
      }}
    >
      <div style={{ flex: '1 1', width: '100%' }}>
        <table className="compute-jobs-table">
          <tbody>
            {sD_computeJobs.slice(0, 10).map((job) => (
              <tr key={job.job_id}>
                <td
                  className="job-name-cell"
                  title={job.job_name}
                  style={{ width: '100%' }}
                >
                  {job.job_name}
                </td>
                <td className="status-cell" style={{ width: '156px' }}>
                  <StatusIndicator status={job.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="form-button-container">
        <PleaseWait text="Loading jobs....." visible={sD_computeJobsLoading} />
        <div style={{ flex: '1 1' }} />
        <MiniButton
          id="refresh-page"
          style={{ flex: '0 0 auto', height: '34px' }}
          onClick={onClickHandler}
          title={t('Refresh')}
          aria-label={t('Refresh')}
        >
          <img src={refreshIcon} alt="Refresh" width="34" height="34" />
        </MiniButton>
      </div>
    </div>
  );
}
